import { useState } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { FinalCta } from "@/components/site/FinalCta";
import { TESTIMONIALS } from "@/data/content";
import { COACHING_STORIES } from "@/data/coaching";
import { fadeUpSmall, staggered } from "@/lib/motion";

const ALL = [
  ...TESTIMONIALS.map((t) => ({ ...t, id: `r${t.id}`, category: t.category || "Recruiting" })),
  ...COACHING_STORIES.map((t) => ({ ...t, category: "Coaching" })),
];
const FILTERS = ["All", "Recruiting", "Coaching"];

const StoryCard = ({ t, i }) => (
  <motion.figure
    data-testid={`story-card-${t.id}`}
    {...staggered(fadeUpSmall, i % 3, 0.06)}
    className="mb-6 break-inside-avoid border border-navy/10 bg-white p-7"
  >
    <Quote className="h-7 w-7 text-navy" fill="currentColor" strokeWidth={0} />
    <blockquote className="mt-4 text-base leading-relaxed text-ink/85">{t.quote}</blockquote>
    <figcaption className="mt-6 flex items-center gap-3 border-t border-navy/10 pt-5">
      <img src={t.photo} alt={t.name} className="h-12 w-12 object-cover" />
      <div>
        <p className="font-heading font-semibold text-ink">{t.name}</p>
        {t.title && <p className="text-sm text-ink/60">{t.title}</p>}
        <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-navy">{t.category}</p>
      </div>
    </figcaption>
  </motion.figure>
);

export default function ClientStories() {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? ALL : ALL.filter((t) => t.category === filter);

  return (
    <main data-testid="client-stories-page" className="bg-white text-ink">
      <Navbar />
      <section className="bg-navy pt-32 pb-14 text-white sm:pt-36 sm:pb-16">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-5 sm:px-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Client Stories</h1>
            <p className="mt-4 font-heading text-lg font-semibold text-white/90 sm:text-xl">Trusted by the leaders who hire and the leaders we coach.</p>
          </div>
          <div className="flex gap-2" data-testid="story-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`story-filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`h-11 px-5 text-sm font-semibold transition-colors ${filter === f ? "bg-white text-navy" : "border border-white/30 text-white hover:bg-white/10"}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-snow py-14 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <p data-testid="stories-count" className="text-sm text-ink/60">{list.length} stories</p>
          <div className="mt-8 columns-1 gap-6 md:columns-2 lg:columns-3">
            {list.map((t, i) => <StoryCard key={t.id} t={t} i={i} />)}
          </div>
        </div>
      </section>
      <FinalCta />
      <Footer />
    </main>
  );
}
